/**
 * The ledger: one way of paying, day by day.
 *
 * Pick cash, card or online and a run of dates, press Get report, and read
 * what came in each day net of what is not the shop's own takings — the
 * riders' delivery charges and the refunds handed back that way. One method
 * at a time, because that is how the owners check it: cash against the
 * drawer, card against the machine, online against the bank.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, formatServerTime, formatServerTimeOnly, today, dateKey } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ReportExport } from './ReportExport.jsx';
import { ReportTabs } from './ReportTabs.jsx';

const METHODS = [
  ['cash', 'Cash sale'],
  ['card', 'Card sale'],
  ['online', 'Online sale'],
];

function startOfMonth(iso) {
  return `${iso.slice(0, 8)}01`;
}

export default function LedgerPage() {
  const [method, setMethod] = useState('cash');
  const [from, setFrom] = useState(() => startOfMonth(today()));
  const [to, setTo] = useState(today);
  // The report is fetched when Get report is pressed, not on every keystroke.
  const [asked, setAsked] = useState(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['reports', 'ledger', asked],
    queryFn: () => api.get(withQuery('/reports/ledger', asked)),
    enabled: Boolean(asked),
  });

  const rows = data?.rows ?? [];
  // Days opened up to show their shifts and bills.
  const [openDays, setOpenDays] = useState(() => new Set());
  const toggleDay = (day) =>
    setOpenDays((was) => {
      const next = new Set(was);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  const totals = data?.totals;
  const input = 'h-12 rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30';
  const bad = from > to;

  return (
    <div className="mx-auto max-w-4xl p-4">
      <ReportTabs />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Ledger</h1>
          <p className="text-sm text-ink-3">One way of paying, day by day, net of delivery charges and refunds</p>
        </div>
        {asked && (
          <ReportExport path={withQuery('/reports/ledger', asked)} />
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">Sale</span>
          <select aria-label="Sale" value={method} onChange={(e) => setMethod(e.target.value)} className={input}>
            {METHODS.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">From</span>
          <input type="date" aria-label="From" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} className={input} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">To</span>
          <input type="date" aria-label="To" value={to} min={from} max={dateKey(new Date())} onChange={(e) => e.target.value && setTo(e.target.value)} className={input} />
        </label>
        <Button size="lg" disabled={bad || isFetching} onClick={() => setAsked({ method, from, to })}>
          Get report
        </Button>
      </div>
      {bad && <p className="mt-2 text-sm text-warn">The start date is after the end date.</p>}

      {!asked ? (
        <p className="py-16 text-center text-sm text-ink-3">Choose a sale type and the dates, then press Get report.</p>
      ) : isLoading ? (
        <div className="flex h-[30vh] items-center justify-center"><Spinner label="Loading" /></div>
      ) : isError ? (
        <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error?.message || 'Could not load the ledger.'}</p>
      ) : (
        <>
          <p className="mt-4 text-sm text-ink-2">
            {data.title}, {data.meta?.[0]?.[1]?.replace(' to ', ' – ')}
          </p>
          <section className="mt-2 overflow-x-auto rounded-xl border border-rule bg-surface">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-rule text-left text-xs text-ink-3">
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-3 py-2.5 font-medium">Sale</th>
                  <th className="px-3 py-2.5 text-right font-medium">Bills</th>
                  <th className="px-3 py-2.5 text-right font-medium">Received</th>
                  <th className="px-3 py-2.5 text-right font-medium">Delivery charges</th>
                  <th className="px-3 py-2.5 text-right font-medium">Refunds</th>
                  <th className="px-4 py-2.5 text-right font-medium">Net sale</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.day}>
                  <tr className={`border-b border-rule/60 last:border-0 ${r.received === 0 ? 'text-ink-3' : ''}`}>
                    <td className="px-4 py-2 tabular-nums text-ink">
                      {r.bills > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggleDay(r.day)}
                          aria-expanded={openDays.has(r.day)}
                          aria-label={`${openDays.has(r.day) ? 'Hide' : 'Show'} the shifts and bills of ${r.date}`}
                          className="flex items-center gap-1.5 text-left hover:text-brand-300"
                        >
                          <Caret open={openDays.has(r.day)} />
                          {r.date}
                        </button>
                      ) : (
                        <span className="pl-5">{r.date}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-2">{r.sale}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">{r.bills || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">{formatPKR(r.received)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">{r.delivery ? `−${formatPKR(r.delivery)}` : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">{r.refunds ? `−${formatPKR(r.refunds)}` : '—'}</td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums text-ink">{formatPKR(r.net)}</td>
                  </tr>
                  {openDays.has(r.day) && (
                    <tr className="border-b border-rule/60 bg-surface-2/40">
                      <td colSpan={7} className="px-4 py-3">
                        <DayBreakdown method={asked.method} day={r.day} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr className="border-t-2 border-rule font-semibold text-ink">
                    <td className="px-4 py-2.5" colSpan={2}>Total</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{totals.bills}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatPKR(totals.received)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{totals.delivery ? `−${formatPKR(totals.delivery)}` : '—'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{totals.refunds ? `−${formatPKR(totals.refunds)}` : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatPKR(totals.net)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </section>
          {data.note && <p className="mt-2 text-xs text-ink-3">{data.note}</p>}
        </>
      )}
    </div>
  );
}

function Caret({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flex: 'none' }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

const METHOD_NAME = { cash: 'Cash', card: 'Card', online: 'Online', wallet: 'Wallet', foodpanda: 'Foodpanda', split: 'Split', complimentary: 'Complimentary' };
const TYPE_NAME = { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery', foodpanda: 'Foodpanda' };
const time = (t) => (t ? formatServerTimeOnly(t) : '—');

/** One ledger day opened up: its shifts, each opening to its bills, each bill to its payments. */
function DayBreakdown({ method, day }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'ledger', 'day', method, day],
    queryFn: () => api.get(withQuery('/reports/ledger/day', { method, day })),
  });
  const [openShifts, setOpenShifts] = useState(() => new Set());
  const toggle = (id) =>
    setOpenShifts((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isLoading) return <Spinner label="Loading the day" />;
  if (isError) return <p role="alert" className="text-sm text-bad">{error?.message || 'Could not load the day.'}</p>;

  return (
    <div className="space-y-2">
      {data.shifts.map((s) => {
        const key = s.id || 'none';
        const open = openShifts.has(key);
        return (
          <div key={key} className="rounded-lg border border-rule bg-surface">
            <button
              type="button"
              onClick={() => toggle(key)}
              aria-expanded={open}
              className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-left text-sm hover:bg-surface-2/60"
            >
              <span className="flex items-center gap-1.5 font-medium text-ink">
                <Caret open={open} />
                {s.shiftNumber ? `${s.shiftNumber}${s.name ? ` · ${s.name}` : ''}` : 'No shift'}
              </span>
              <span className="text-xs text-ink-3">
                {s.cashier || ''}
                {s.openedAt ? ` · ${formatServerTime(s.openedAt)}` : ''}
                {s.closedAt ? ` to ${formatServerTime(s.closedAt)}` : s.id ? ' · still open' : ''}
              </span>
              <span className="ml-auto flex gap-4 text-xs tabular-nums text-ink-2">
                <span>{s.bills.length} bill{s.bills.length === 1 ? '' : 's'}</span>
                <span>Received {formatPKR(s.received)}</span>
                {s.delivery > 0 && <span>Delivery −{formatPKR(s.delivery)}</span>}
                {s.refunds > 0 && <span>Refunds −{formatPKR(s.refunds)}</span>}
                <span className="font-medium text-ink">Net {formatPKR(s.net)}</span>
              </span>
            </button>
            {open && (
              <ul className="border-t border-rule">
                {s.bills.map((b) => (
                  <BillLine key={b.id} bill={b} method={method} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {data.refunds.length > 0 && (
        <div className="rounded-lg border border-rule bg-surface px-3 py-2 text-xs text-ink-2">
          <div className="mb-1 font-medium text-ink">Refunds handed back this way</div>
          {data.refunds.map((r) => (
            <div key={r.refundNumber} className="flex justify-between gap-3">
              <span>
                {r.refundNumber} · bill {r.orderNumber} · {time(r.at)} · {r.reason}
              </span>
              <span className="tabular-nums">−{formatPKR(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** One bill, opening to everything paid on it. */
function BillLine({ bill, method }) {
  const [open, setOpen] = useState(false);
  const paidOther = bill.payments.filter((p) => p.method !== method).reduce((t, p) => t + p.amount, 0);
  return (
    <li className="border-b border-rule/60 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} the payments on bill ${bill.orderNumber}`}
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-0.5 px-3 py-1.5 pl-7 text-left text-sm hover:bg-surface-2/60"
      >
        <span className="flex items-center gap-1.5 text-ink">
          <Caret open={open} />
          {bill.orderNumber}
        </span>
        <span className="text-xs text-ink-3">
          {TYPE_NAME[bill.orderType] || bill.orderType} · paid {time(bill.paidAt)}
          {bill.customer ? ` · ${bill.customer}` : ''}
          {paidOther > 0 ? ' · split' : ''}
        </span>
        <span className="ml-auto tabular-nums text-ink-2">{formatPKR(bill.received)}</span>
      </button>
      {open && (
        <div className="mx-7 mb-2 rounded-md border border-rule bg-surface-2/40 px-3 py-2 text-xs text-ink-2">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 sm:grid-cols-4">
            <span>Taken {time(bill.takenAt)}</span>
            <span>Bill total {formatPKR(bill.total)}</span>
            <span>Discount {bill.discount ? `−${formatPKR(bill.discount)}` : '—'}</span>
            <span>Delivery charge {bill.deliveryFee ? formatPKR(bill.deliveryFee) : '—'}</span>
          </div>
          <table className="mt-2 w-full">
            <thead>
              <tr className="text-left text-ink-3">
                <th className="py-0.5 font-medium">Paid by</th>
                <th className="py-0.5 font-medium">When</th>
                <th className="py-0.5 text-right font-medium">Amount</th>
                <th className="py-0.5 text-right font-medium">Handed over</th>
                <th className="py-0.5 text-right font-medium">Change</th>
                <th className="py-0.5 pl-3 font-medium">Reference</th>
              </tr>
            </thead>
            <tbody>
              {bill.payments.map((p, i) => (
                <tr key={i} className={p.method === method ? 'text-ink' : ''}>
                  <td className="py-0.5">{METHOD_NAME[p.method] || p.method}</td>
                  <td className="py-0.5">{time(p.at)}</td>
                  <td className="py-0.5 text-right tabular-nums">{formatPKR(p.amount)}</td>
                  <td className="py-0.5 text-right tabular-nums">{p.tendered ? formatPKR(p.tendered) : '—'}</td>
                  <td className="py-0.5 text-right tabular-nums">{p.change ? formatPKR(p.change) : '—'}</td>
                  <td className="py-0.5 pl-3">{p.reference || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {bill.delivery > 0 && <p className="mt-1 text-ink-3">Of what came in this way, {formatPKR(bill.delivery)} is the rider&rsquo;s delivery charge.</p>}
        </div>
      )}
    </li>
  );
}
